import { Button } from '@mantine/core';
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';
import { useCallback, useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { isCodeEditorViewAtom } from '~/atoms/atoms';
import clsx from 'clsx';

export function ScrollToStepButton() {
  const { recorderState, vmStatus, recorderInitiator } = useLiveStateManager();
  const isCodeEditorView = useAtomValue(isCodeEditorViewAtom);
  const currentStepId = vmStatus?.executionState?.currentStepId ?? recorderState?.stepId;
  const [isStepVisible, setIsStepVisible] = useState(true);
  const [isStepAbove, setIsStepAbove] = useState(false);

  // Intersection observer to detect if the current step is visible
  useEffect(() => {
    // If the step is not loaded, or we're in code editor view, we don't need to show the button
    if (!currentStepId || isCodeEditorView) {
      setIsStepVisible(true);
      return;
    }

    // Every step will have an id of step-<stepId> specified in Step.tsx
    // We need an ID for the observer & scrolling to the step to work
    // Fallback to the recorder initiator's stepId if the current step is not loaded
    const vmStatusStateIsReady = vmStatus?.state !== 'loading';
    const stepElement =
      vmStatusStateIsReady &&
      (document.getElementById(`step-${currentStepId}`) ??
        document.getElementById(`step-${recorderInitiator?.location?.stepId}`));

    if (!stepElement) {
      setIsStepVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsStepVisible(true);
        } else {
          setIsStepVisible(false);

          // Determine if the step is above or below the viewport
          const stepRect = stepElement.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          setIsStepAbove(stepRect.top < viewportHeight / 2);
        }
      },
      {
        root: null,
        rootMargin: '-100px 0px 0px 0px', // top margin to account for control bar
        threshold: 0.1, // at least 10% of step visible
      },
    );

    observer.observe(stepElement);

    return () => {
      observer.disconnect();
    };
  }, [currentStepId, isCodeEditorView, recorderInitiator]);

  // There is a useEffect in StepList.tsx that scrolls to the current step when the stepIdToScrollTo atom changes
  // It is snappier to use this implementation instead...
  const scrollToCurrentStep = useCallback(() => {
    if (currentStepId) {
      const step = document.getElementById(`step-${currentStepId}`);

      step?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentStepId]);

  return (
    !isStepVisible &&
    (recorderState?.executionType === 'record' || recorderState?.executionType === 'preview') && (
      <div
        className={clsx('absolute left-0 right-0 z-[52] flex justify-center', {
          'top-32': isStepAbove,
          'bottom-8': !isStepAbove,
        })}
      >
        <Button
          variant="filled"
          className="rounded-full font-medium shadow-md"
          onClick={scrollToCurrentStep}
          leftSection={isStepAbove ? <IconArrowUp size={16} /> : <IconArrowDown size={16} />}
        >
          Jump to cursor
        </Button>
      </div>
    )
  );
}
